import React, { useRef, useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { X, Search, UserPlus, Users, UploadCloud, FileSpreadsheet } from 'lucide-react'
import { useTheme } from '@/context/ThemeContext'
import { searchContactsQuick, type QuickContact } from '@/features/hubspot/services/hubspotApi'
import { parseCsvRecipients } from '../utils/recipients'
import type { CampaignRecipientInput } from '../types'

type Props = {
  workspaceId: string
  recipients: CampaignRecipientInput[]
  onChange: (recipients: CampaignRecipientInput[]) => void
}

const EMAIL_RE = /[^\s<>,]+@[^\s<>,]+\.[^\s<>,]+/
// Collapses the combined list behind a "View all" toggle past this many — a
// campaign can easily have hundreds of recipients, and rendering/scrolling all of
// them inline would make the builder itself feel sluggish.
const VISIBLE_LIMIT = 24

// Accepts one recipient per line, in any of:
//   jane@example.com
//   jane@example.com, Jane, Doe
//   Jane Doe <jane@example.com>
// Anything else on a line is ignored rather than rejected outright — a rep pasting
// a CSV export's header row or a stray blank line shouldn't block the whole paste.
function parseManualRecipients(text: string): { parsed: CampaignRecipientInput[]; skippedLines: string[] } {
  const parsed: CampaignRecipientInput[] = []
  const skippedLines: string[] = []

  for (const rawLine of text.split('\n')) {
    const line = rawLine.trim()
    if (!line) continue

    const angleMatch = line.match(/^(.*)<([^>]+)>$/)
    if (angleMatch) {
      const email = angleMatch[2]!.trim()
      if (!EMAIL_RE.test(email)) { skippedLines.push(line); continue }
      const [firstName, ...rest] = angleMatch[1]!.trim().split(/\s+/).filter(Boolean)
      parsed.push({ email, ...(firstName ? { firstName } : {}), ...(rest.length ? { lastName: rest.join(' ') } : {}) })
      continue
    }

    if (line.includes(',')) {
      const [email, firstName, lastName] = line.split(',').map((p) => p.trim())
      if (!email || !EMAIL_RE.test(email)) { skippedLines.push(line); continue }
      parsed.push({ email, ...(firstName ? { firstName } : {}), ...(lastName ? { lastName } : {}) })
      continue
    }

    if (EMAIL_RE.test(line)) {
      parsed.push({ email: line })
      continue
    }

    skippedLines.push(line)
  }

  return { parsed, skippedLines }
}

type RecipientMethod = 'manual' | 'hubspot' | 'csv'

const METHODS: Array<{ key: RecipientMethod; label: string; icon: React.ElementType }> = [
  { key: 'manual', label: 'Add manually', icon: UserPlus },
  { key: 'hubspot', label: 'From HubSpot', icon: Search },
  { key: 'csv', label: 'Upload CSV', icon: FileSpreadsheet },
]

const RecipientPicker: React.FC<Props> = ({ workspaceId, recipients, onChange }) => {
  const { currentTheme: t } = useTheme()
  const [activeMethod, setActiveMethod] = useState<RecipientMethod>('manual')
  const [manualText, setManualText] = useState('')
  const [skippedLines, setSkippedLines] = useState<string[]>([])
  const [hubspotQuery, setHubspotQuery] = useState('')
  const [hubspotResults, setHubspotResults] = useState<QuickContact[]>([])
  const [hubspotLoading, setHubspotLoading] = useState(false)
  const [hubspotError, setHubspotError] = useState<string | null>(null)
  const [csvSummary, setCsvSummary] = useState<{ added: number; skipped: number } | null>(null)
  const [csvError, setCsvError] = useState<string | null>(null)
  const [isDraggingCsv, setIsDraggingCsv] = useState(false)
  const [showAll, setShowAll] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const existingEmails = new Set(recipients.map((r) => r.email.toLowerCase()))

  const addRecipients = (toAdd: CampaignRecipientInput[]) => {
    const deduped = toAdd.filter((r) => !existingEmails.has(r.email.toLowerCase()))
    if (deduped.length > 0) onChange([...recipients, ...deduped])
    return deduped.length
  }

  const handleAddManual = () => {
    const { parsed, skippedLines: skipped } = parseManualRecipients(manualText)
    addRecipients(parsed)
    setSkippedLines(skipped)
    setManualText('')
  }

  const handleHubspotSearch = (query: string) => {
    setHubspotQuery(query)
    if (!query.trim()) {
      setHubspotResults([])
      return
    }
    setHubspotLoading(true)
    setHubspotError(null)
    searchContactsQuick(workspaceId, query.trim())
      .then((results) => setHubspotResults(results.filter((r) => r.email)))
      .catch((err) => setHubspotError(err instanceof Error ? err.message : 'Search failed'))
      .finally(() => setHubspotLoading(false))
  }

  const addHubspotContact = (contact: QuickContact) => {
    const [firstName, ...rest] = contact.name.split(/\s+/).filter((p) => p && p !== 'Unnamed')
    addRecipients([{
      email: contact.email,
      ...(firstName ? { firstName } : {}),
      ...(rest.length ? { lastName: rest.join(' ') } : {}),
      hubspotContactId: contact.id,
    }])
  }

  const removeRecipient = (email: string) => {
    onChange(recipients.filter((r) => r.email.toLowerCase() !== email.toLowerCase()))
  }

  const handleCsvFile = (file: File) => {
    setCsvError(null)
    setCsvSummary(null)
    if (!file.name.toLowerCase().endsWith('.csv') && file.type !== 'text/csv') {
      setCsvError('That file doesn\'t look like a .csv — try exporting as CSV first.')
      return
    }
    const reader = new FileReader()
    reader.onload = () => {
      const text = typeof reader.result === 'string' ? reader.result : ''
      const { parsed, skippedRows } = parseCsvRecipients(text)
      const added = addRecipients(parsed)
      setCsvSummary({ added, skipped: skippedRows.length })
    }
    reader.onerror = () => setCsvError('Couldn\'t read that file.')
    reader.readAsText(file)
  }

  const visibleRecipients = showAll ? recipients : recipients.slice(0, VISIBLE_LIMIT)
  const hiddenCount = recipients.length - visibleRecipients.length

  return (
    <div className="flex flex-col gap-4">
      {/* Pick how to add recipients — only the selected method's panel shows
          below, instead of all three competing for attention at once. Same
          segmented-pill look as the Plain text/HTML toggle elsewhere in this form. */}
      <div className="inline-flex items-center gap-1 rounded-lg p-1 self-start" style={{ backgroundColor: t.hoverBackground, border: `1px solid ${t.border}` }}>
        {METHODS.map(({ key, label, icon: Icon }) => {
          const isActive = activeMethod === key
          return (
            <button
              key={key}
              type="button"
              onClick={() => setActiveMethod(key)}
              className="flex items-center gap-1.5 h-7 px-2.5 rounded-md text-[11px] font-medium transition-colors"
              style={{ backgroundColor: isActive ? t.accent : 'transparent', color: isActive ? t.buttonText : t.secondrytext }}
            >
              <Icon size={11} />
              {label}
            </button>
          )
        })}
      </div>

      {activeMethod === 'manual' && (
        <div className="flex flex-col gap-2">
          <textarea
            value={manualText}
            onChange={(e) => setManualText(e.target.value)}
            placeholder={'One per line:\njane@example.com\nJane Doe <jane@example.com>'}
            className="w-full min-h-[90px] text-[13px] px-2.5 py-2 rounded-lg border outline-none resize-y"
            style={{ backgroundColor: t.hoverBackground, borderColor: t.border, color: t.text }}
          />
          <button
            type="button"
            onClick={handleAddManual}
            disabled={!manualText.trim()}
            className="self-start flex items-center gap-1.5 h-8 px-3 rounded-lg text-xs font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ backgroundColor: t.hoverBackground, color: t.text, border: `1px solid ${t.border}` }}
          >
            Add to list
          </button>
          {skippedLines.length > 0 && (
            <p className="text-[11.5px]" style={{ color: t.warningText }}>
              Skipped {skippedLines.length} line{skippedLines.length === 1 ? '' : 's'} that didn&apos;t look like an email: {skippedLines.slice(0, 3).join(', ')}{skippedLines.length > 3 ? '…' : ''}
            </p>
          )}
        </div>
      )}

      {activeMethod === 'hubspot' && (
        <div className="flex flex-col gap-2">
          <input
            value={hubspotQuery}
            onChange={(e) => handleHubspotSearch(e.target.value)}
            placeholder="Search contacts by name or email…"
            className="w-full h-11 text-sm px-3.5 rounded-lg border outline-none"
            style={{ backgroundColor: t.hoverBackground, borderColor: t.border, color: t.text }}
          />
          <div className="flex flex-col gap-1 max-h-[140px] overflow-y-auto custom-scrollbar">
            {hubspotLoading && <span className="text-[12px]" style={{ color: t.mutedText }}>Searching…</span>}
            {hubspotError && <span className="text-[12px]" style={{ color: t.errorText }}>{hubspotError}</span>}
            {!hubspotLoading && !hubspotError && hubspotQuery.trim() && hubspotResults.length === 0 && (
              <span className="text-[12px]" style={{ color: t.mutedText }}>No matching contacts.</span>
            )}
            {hubspotResults.map((contact) => {
              const alreadyAdded = existingEmails.has(contact.email.toLowerCase())
              return (
                <button
                  key={contact.id}
                  type="button"
                  disabled={alreadyAdded}
                  onClick={() => addHubspotContact(contact)}
                  className="flex items-center justify-between gap-2 px-2.5 py-1.5 rounded-lg text-left text-[12.5px] disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ backgroundColor: t.hoverBackground, color: t.text }}
                >
                  <span className="truncate">{contact.name} · {contact.email}</span>
                  {alreadyAdded && <span className="text-[11px] shrink-0" style={{ color: t.mutedText }}>Added</span>}
                </button>
              )
            })}
          </div>
        </div>
      )}

      {activeMethod === 'csv' && (
        <div className="flex flex-col gap-2">
          <div
            onDragOver={(e) => { e.preventDefault(); setIsDraggingCsv(true) }}
            onDragLeave={() => setIsDraggingCsv(false)}
            onDrop={(e) => {
              e.preventDefault()
              setIsDraggingCsv(false)
              const file = e.dataTransfer.files[0]
              if (file) handleCsvFile(file)
            }}
            onClick={() => fileInputRef.current?.click()}
            className="flex items-center justify-center gap-2 h-20 rounded-lg border-2 border-dashed cursor-pointer transition-colors"
            style={{
              borderColor: isDraggingCsv ? t.accent : t.border,
              backgroundColor: isDraggingCsv ? t.hoverBackground : 'transparent',
            }}
          >
            <UploadCloud size={18} style={{ color: t.mutedText }} />
            <div className="text-center">
              <p className="text-[12.5px] font-medium" style={{ color: t.text }}>
                Drop a .csv here, or click to browse
              </p>
              <p className="text-[11px]" style={{ color: t.mutedText }}>
                Columns: email, firstName, lastName (header optional)
              </p>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,text/csv"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0]
                if (file) handleCsvFile(file)
                e.target.value = ''
              }}
            />
          </div>
          {csvError && <p className="text-[11.5px]" style={{ color: t.errorText }}>{csvError}</p>}
          {csvSummary && (
            <p className="text-[11.5px]" style={{ color: csvSummary.skipped > 0 ? t.warningText : t.successText }}>
              Added {csvSummary.added} recipient{csvSummary.added === 1 ? '' : 's'} from the file
              {csvSummary.skipped > 0 ? ` — skipped ${csvSummary.skipped} row${csvSummary.skipped === 1 ? '' : 's'} that didn't look valid.` : '.'}
            </p>
          )}
        </div>
      )}

      {/* Combined recipient list */}
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-1.5">
          <Users size={13} style={{ color: t.mutedText }} />
          <span className="text-[12px] font-semibold" style={{ color: t.text }}>
            {recipients.length} recipient{recipients.length === 1 ? '' : 's'}
          </span>
        </div>
        {recipients.length === 0 ? (
          <p className="text-[12.5px]" style={{ color: t.mutedText }}>No recipients added yet.</p>
        ) : (
          <>
            <motion.div layout className="flex flex-wrap gap-1.5 max-h-[220px] overflow-y-auto custom-scrollbar">
              <AnimatePresence initial={false}>
                {visibleRecipients.map((r) => (
                  <motion.span
                    key={r.email}
                    layout
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.9 }}
                    transition={{ duration: 0.15 }}
                    className="flex items-center gap-1.5 pl-2.5 pr-1.5 py-1 rounded-full text-[12px]"
                    style={{ backgroundColor: t.hoverBackground, color: t.text, border: `1px solid ${t.border}` }}
                  >
                    {r.firstName ? `${r.firstName} — ` : ''}{r.email}
                    <button
                      type="button"
                      onClick={() => removeRecipient(r.email)}
                      aria-label={`Remove ${r.email}`}
                      className="p-0.5 rounded-full"
                      style={{ color: t.mutedText }}
                    >
                      <X size={11} />
                    </button>
                  </motion.span>
                ))}
              </AnimatePresence>
            </motion.div>
            {(hiddenCount > 0 || showAll) && recipients.length > VISIBLE_LIMIT && (
              <button
                type="button"
                onClick={() => setShowAll((v) => !v)}
                className="self-start text-[12px] font-medium"
                style={{ color: t.accent }}
              >
                {showAll ? 'Show less' : `View all (${recipients.length})`}
              </button>
            )}
          </>
        )}
      </div>
    </div>
  )
}

export default React.memo(RecipientPicker)
